import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchEntries as fetchEntriesApi } from './content-feed.api.ts';
import type { ContentFeedEntryResolved, ContentFeedListResponse } from './content-feed.types.ts';
type FetchFn = (params: {
  cursor: string | null;
}) => Promise<ContentFeedListResponse>;

/**
 * Hook that manages the paginated content feed state.
 * Handles fetching entries, pagination cursor, and loading state.
 */
export const useContentFeed = (fetchOverride?: FetchFn) => {
  const {
    t,
    i18n
  } = useTranslation(['my']);
  const [entries, setEntries] = useState<ContentFeedEntryResolved[]>([]);
  const [cursor, setCursor] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hasMore, setHasMore] = useState(true);
  const locale = i18n.language;
  const fetchEntries: FetchFn = fetchOverride ? fetchOverride : params => fetchEntriesApi({
    ...params,
    locale
  });

  // Initial load (and re-fetch on locale change)
  useEffect(() => {
    let cancelled = false;
    fetchEntries({
      cursor: null
    }).then(response => {
      if (!cancelled) {
        setEntries(response.entries);
        setCursor(response.cursor);
        setHasMore(response.cursor !== null);
      }
    }).catch(() => {
      if (!cancelled) {
        setEntries([]);
        setHasMore(false);
      }
    }).finally(() => {
      if (!cancelled) {
        setIsLoading(false);
      }
    });
    return () => {
      cancelled = true;
    };
    // fetchEntries is rebuilt on every render (closes over locale); including
    // it in deps would cause an infinite loop. The locale dep already triggers
    // re-fetch when language changes.
  }, [locale]);
  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) {
      return;
    }
    setIsLoading(true);
    try {
      const response = await fetchEntries({
        cursor
      });
      setEntries(prev => [...prev, ...response.entries]);
      setCursor(response.cursor);
      setHasMore(response.cursor !== null);
    } finally {
      setIsLoading(false);
    }
  }, [cursor, fetchEntries, hasMore, isLoading]);
  return {
    entries,
    isLoading,
    hasMore,
    loadMore,
    t
  };
};